import { useState, useEffect, useCallback } from "react";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Shirt, DollarSign, TrendingUp, Calendar as CalendarIcon, ChevronLeft, ChevronRight, TrendingDown, Flame } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, addMonths, subMonths, parseISO } from "date-fns";
import { toast } from "sonner";
import { authFetch } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Streak Indicator Component - Small, subtle, non-intrusive
const StreakIndicator = ({ streak, uploadedToday }) => {
  if (streak === 0 && !uploadedToday) return null;
  
  const showFire = streak >= 3;
  
  return (
    <div 
      className="flex items-center gap-1 text-sm text-muted-foreground"
      title={`${streak} day upload streak${uploadedToday ? ' - Logged today!' : ''}`}
      data-testid="streak-indicator"
    >
      {showFire && <Flame className="w-3.5 h-3.5 text-orange-500" />}
      <span className={`font-medium ${showFire ? 'text-orange-600' : 'text-muted-foreground'}`}>
        {streak}
      </span>
    </div>
  );
};

export function Dashboard() {
  const [stats, setStats] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [wardrobeValue, setWardrobeValue] = useState(null);
  const [streakInfo, setStreakInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, calendarRes, valueRes, streakRes] = await Promise.all([
        authFetch(`${API_URL}/api/dashboard/stats`),
        authFetch(`${API_URL}/api/dashboard/calendar?months=3`),
        authFetch(`${API_URL}/api/dashboard/wardrobe-value`),
        authFetch(`${API_URL}/api/streak`)
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (calendarRes.ok) setCalendarData(await calendarRes.json());
      if (valueRes.ok) setWardrobeValue(await valueRes.json());
      if (streakRes.ok) setStreakInfo(await streakRes.json());
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  const statCards = [
    {
      title: "Total Items",
      value: stats?.total_items || 0,
      icon: Shirt,
      color: "bg-sand-100",
    },
    {
      title: "Original Value",
      value: `$${(wardrobeValue?.original_value || stats?.total_value || 0).toLocaleString()}`,
      icon: DollarSign,
      color: "bg-sand-100",
    },
    {
      title: "Market Value",
      value: `$${(wardrobeValue?.estimated_market_value || 0).toLocaleString()}`,
      subtitle: wardrobeValue?.depreciation_percentage ? `${wardrobeValue.depreciation_percentage}% depreciation` : null,
      icon: TrendingDown,
      color: "bg-terracotta-500/10",
    },
    {
      title: "Avg Cost/Wear",
      value: `$${(stats?.avg_cost_per_wear || 0).toFixed(2)}`,
      icon: TrendingUp,
      color: "bg-sand-100",
    },
  ];

  const chartData = (stats?.best_value_items || []).map(item => ({
    name: `${item.brand} ${item.item_name}`.substring(0, 15),
    costPerWear: item.cost_per_wear,
    wears: item.wear_count,
  }));

  // Calendar rendering
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Pad start of month to align with day of week
  const startDay = monthStart.getDay();
  const paddedDays = [...Array(startDay).fill(null), ...days];

  const getLogForDate = (date) => {
    if (!date || !calendarData?.calendar_data) return null;
    const dateStr = format(date, "yyyy-MM-dd");
    return calendarData.calendar_data[dateStr];
  };

  return (
    <Layout>
      <div className="space-y-10 md:space-y-12" data-testid="dashboard-page">
        {/* Page Header with Streak Indicator */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight text-primary">
              Dashboard
            </h1>
            <p className="text-muted-foreground mt-1">
              Your wardrobe at a glance
            </p>
          </div>
          {/* Streak Indicator - Small, subtle, top-right */}
          {streakInfo && (
            <StreakIndicator 
              streak={streakInfo.current_streak} 
              uploadedToday={streakInfo.uploaded_today}
            />
          )}
        </div>

        {/* Stats Grid - Minimal cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {statCards.map((stat) => (
            <Card 
              key={stat.title} 
              className="border border-border/50 shadow-none rounded-lg"
              data-testid={`stat-${stat.title.toLowerCase().replace(/[^a-z]/g, '-')}`}
            >
              <CardContent className="p-5 md:p-6">
                <p className="text-xs text-muted-foreground mb-2">
                  {stat.title}
                </p>
                <p className="font-display text-2xl md:text-3xl text-primary">
                  {stat.value}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
          {/* Best Value Items Chart */}
          <Card className="lg:col-span-2 border border-border shadow-none">
            <CardHeader className="p-6 pb-2">
              <CardTitle className="font-heading text-xl font-medium">
                Best Value Items
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Lowest cost per wear
              </p>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              {chartData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 20 }}>
                      <XAxis type="number" tickFormatter={(v) => `$${v}`} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" width={100} axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                      <Tooltip 
                        formatter={(value) => [`$${value.toFixed(2)}`, "Cost/Wear"]}
                        contentStyle={{ 
                          backgroundColor: "#1A1A1A", 
                          border: "none", 
                          borderRadius: "2px",
                          color: "#fff"
                        }}
                        labelStyle={{ color: "#fff" }}
                      />
                      <Bar dataKey="costPerWear" fill="#722F37" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  <p>No items worn yet. Start logging your outfits!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="border border-border shadow-none">
            <CardHeader className="p-6 pb-2">
              <CardTitle className="font-heading text-xl font-medium">
                Recent Activity
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Latest items worn
              </p>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              {(stats?.recent_wears || []).length > 0 ? (
                <div className="space-y-4">
                  {stats.recent_wears.slice(0, 6).map((wear, idx) => (
                    <div 
                      key={`${wear.date}-${wear.item_id}-${idx}`}
                      className="flex items-center gap-3 pb-3 border-b border-border last:border-0 last:pb-0"
                    >
                      <div className="w-10 h-10 bg-sand-100 flex items-center justify-center flex-shrink-0">
                        <Shirt className="w-5 h-5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate">
                          {wear.brand} {wear.item_name}
                        </p>
                        <p className="text-xs text-muted-foreground">{wear.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No recent activity</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Outfit History Calendar */}
        <Card className="border border-border shadow-none" data-testid="outfit-calendar">
          <CardHeader className="p-6 pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-xl font-medium">
                  Outfit History
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  {calendarData?.total_logged_days || 0} days logged
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  data-testid="calendar-prev-month"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="font-medium text-sm min-w-[120px] text-center">
                  {format(currentMonth, "MMMM yyyy")}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  data-testid="calendar-next-month"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 pt-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div key={day} className="text-center text-xs font-semibold text-muted-foreground py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1">
              {paddedDays.map((day, idx) => {
                if (!day) {
                  return <div key={`empty-${idx}`} className="aspect-square" />;
                }
                
                const log = getLogForDate(day);
                const hasItems = log && log.items_count > 0;
                const isSelected = selectedDate && format(selectedDate, "yyyy-MM-dd") === format(day, "yyyy-MM-dd");
                
                return (
                  <button
                    key={format(day, "yyyy-MM-dd")}
                    onClick={() => setSelectedDate(hasItems ? day : null)}
                    className={`aspect-square flex flex-col items-center justify-center rounded-sm text-sm transition-colors relative
                      ${isToday(day) ? "ring-1 ring-terracotta-500" : ""}
                      ${hasItems ? "bg-terracotta-500/10 hover:bg-terracotta-500/20 cursor-pointer" : "hover:bg-sand-50"}
                      ${isSelected ? "bg-terracotta-500 text-white hover:bg-terracotta-600" : ""}
                      ${!isSameMonth(day, currentMonth) ? "text-muted-foreground/50" : ""}
                    `}
                    data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                  >
                    <span className={`${hasItems && !isSelected ? "font-medium" : ""}`}>
                      {format(day, "d")}
                    </span>
                    {hasItems && !isSelected && (
                      <span className="text-[10px] text-terracotta-500 font-medium">
                        {log.items_count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selected date details */}
            {selectedDate && getLogForDate(selectedDate) && (
              <div className="mt-4 p-4 bg-sand-50 rounded-sm animate-fade-in">
                <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">
                  {format(selectedDate, "EEEE, MMMM d, yyyy")}
                </p>
                <div className="space-y-2">
                  {getLogForDate(selectedDate).items.map((item) => (
                    <div key={item.item_id} className="flex items-center gap-2">
                      <Shirt className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">
                        <span className="font-medium">{item.brand}</span> {item.item_name}
                      </span>
                      <span className="text-xs text-muted-foreground capitalize">({item.category})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Wardrobe by Category */}
        {stats?.wardrobe_by_category && Object.keys(stats.wardrobe_by_category).length > 0 && (
          <Card className="border border-border shadow-none">
            <CardHeader className="p-6 pb-2">
              <CardTitle className="font-heading text-xl font-medium">
                Wardrobe Breakdown
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Items by category
              </p>
            </CardHeader>
            <CardContent className="p-6 pt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(stats.wardrobe_by_category).map(([category, data]) => (
                  <div key={category} className="p-4 bg-sand-50 rounded-sm">
                    <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-1 capitalize">
                      {category}
                    </p>
                    <p className="font-heading text-2xl font-medium text-primary">
                      {data.count}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      ${data.value.toLocaleString()} value
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
